import {
  Component,
  ElementRef,
  Injector,
  afterNextRender,
  computed,
  inject,
  input,
  linkedSignal,
  output,
} from '@angular/core'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { monthGrid } from '@seiri/shared/common'
import { DateTime, Info } from 'luxon'
import { CalendarView } from '../date-time-picker.types'

/** The UI is Slovak whatever the browser's locale, so names come from `sk`, not from `LOCALE_ID`. */
const LOCALE = 'sk'
const MONTH_NAMES = Info.months('long', { locale: LOCALE })
const MONTH_SHORT_NAMES = Info.months('short', { locale: LOCALE })
const WEEKDAY_NAMES = Info.weekdays('short', { locale: LOCALE })
/** The years view shows a page of 24 years, 4 × 6; the page holding a year starts at `year - year % 24`. */
const YEARS_PER_PAGE = 24

/** A month as one number, `year * 12 + month` (0-based month), so stepping months is plain arithmetic. */
const monthIndexOf = (date: Date) => date.getFullYear() * 12 + date.getMonth()
/** A calendar day as one comparable number, in the browser's zone. */
const dayKeyOf = (date: Date) => date.getFullYear() * 10000 + date.getMonth() * 100 + date.getDate()

interface DayCell {
  date: Date
  day: number
  inMonth: boolean
  disabled: boolean
  /** Today. */
  current: boolean
  selected: boolean
  label: string
}

interface PeriodCell {
  value: number
  text: string
  label: string
  disabled: boolean
  current: boolean
  selected: boolean
}

/**
 * The bare calendar: a month of days, with month ▾ and year ▾ dropdowns that open a months and a years page. It draws
 * no surface, shadow or padding, so it can sit inline on a page or inside `seiri-date-time-picker-panel`. Its API
 * mirrors `mat-calendar`'s (`selected`, `min`, `max`, `startAt`, `startView`, `selectedChange`).
 *
 * Zone-free: it reads and emits browser-local wall-clock `Date`s, and a picked day is emitted at local midnight. A
 * caller showing a premise's days converts with `getUTCintoTimezone` before and `getDateInTimezone` after. An input
 * `Date` is never mutated.
 *
 * - Weeks start on Monday, and the grid is always 6 rows. Days of the neighbouring months are shown muted, for
 *   context only. Days, months and years wholly outside `min`/`max` are disabled, and the arrows stop at them.
 * - Month ▾ opens the months page and year ▾ the years page; a pick returns to the days and leaves the other part as
 *   it was. On the years page the arrows move 24 years; on the months page they are hidden.
 * - `startView="years"` (birth dates): with no `selected`, it opens on the years page around `startAt`, and then
 *   chains year → months → days. Once there is a value it opens on that value's month.
 */
@Component({
  selector: 'seiri-date-calendar',
  imports: [MatButtonModule, MatIconModule],
  templateUrl: './date-calendar.component.html',
  styleUrl: './date-calendar.component.css',
  host: { class: 'seiri-date-calendar' },
})
export class DateCalendarComponent {
  readonly selected = input<Date | null | undefined>(null)
  readonly min = input<Date | null | undefined>(null)
  readonly max = input<Date | null | undefined>(null)
  /** The month to open on when there is no `selected`; today by default. */
  readonly startAt = input<Date | null | undefined>(null)
  readonly startView = input<'days' | 'years'>('days')
  readonly selectedChange = output<Date>()

  protected readonly weekdays = WEEKDAY_NAMES

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef)
  private readonly injector = inject(Injector)
  private readonly today = new Date()

  private readonly hasSelected = computed(() => !!this.selected())
  /** The month the calendar opens on. A number, so a new `Date` on the same month (a changed time) does not reset it. */
  private readonly anchorMonth = computed(() => monthIndexOf(this.selected() ?? this.startAt() ?? this.today))

  /** The month the days page shows, and whose year the months page and the year ▾ show. */
  protected readonly visibleMonth = linkedSignal(() => this.anchorMonth())
  protected readonly view = linkedSignal<CalendarView>(() =>
    this.startView() === 'years' && !this.hasSelected() ? 'years' : 'days',
  )
  /** Whether a year pick goes on to the months page (the birth-date flow) rather than back to the days. */
  private readonly chained = linkedSignal(() => this.startView() === 'years' && !this.hasSelected())

  protected readonly visibleYear = computed(() => Math.floor(this.visibleMonth() / 12))
  protected readonly monthName = computed(() => MONTH_NAMES[this.visibleMonth() % 12])
  private readonly pageStart = computed(() => this.visibleYear() - (this.visibleYear() % YEARS_PER_PAGE))

  private readonly minDay = computed(() => this.dayKeyOrNull(this.min()))
  private readonly maxDay = computed(() => this.dayKeyOrNull(this.max()))

  protected readonly days = computed<DayCell[]>(() => {
    const month = this.visibleMonth() % 12
    const selectedDay = this.dayKeyOrNull(this.selected())
    const todayKey = dayKeyOf(this.today)
    return monthGrid(new Date(this.visibleYear(), month, 1)).map((date) => {
      const key = dayKeyOf(date)
      return {
        date,
        day: date.getDate(),
        inMonth: date.getMonth() === month,
        disabled: this.isDayOutOfRange(key),
        current: key === todayKey,
        selected: key === selectedDay,
        label: DateTime.fromJSDate(date).setLocale(LOCALE).toLocaleString(DateTime.DATE_FULL),
      }
    })
  })

  protected readonly months = computed<PeriodCell[]>(() => {
    const year = this.visibleYear()
    const selected = this.selected()
    return MONTH_SHORT_NAMES.map((text, month) => ({
      value: month,
      text,
      label: `${MONTH_NAMES[month]} ${year}`,
      disabled: this.isRangeOutOfBounds(new Date(year, month, 1), new Date(year, month + 1, 0)),
      current: year === this.today.getFullYear() && month === this.today.getMonth(),
      selected: !!selected && year === selected.getFullYear() && month === selected.getMonth(),
    }))
  })

  protected readonly years = computed<PeriodCell[]>(() => {
    const start = this.pageStart()
    const selectedYear = this.selected()?.getFullYear()
    return Array.from({ length: YEARS_PER_PAGE }, (_, offset) => {
      const year = start + offset
      return {
        value: year,
        text: String(year),
        label: String(year),
        disabled: this.isRangeOutOfBounds(new Date(year, 0, 1), new Date(year, 11, 31)),
        current: year === this.today.getFullYear(),
        selected: year === selectedYear,
      }
    })
  })

  protected readonly previousLabel = computed(() =>
    this.view() === 'years' ? `Predchádzajúcich ${YEARS_PER_PAGE} rokov` : 'Predchádzajúci mesiac',
  )
  protected readonly nextLabel = computed(() =>
    this.view() === 'years' ? `Nasledujúcich ${YEARS_PER_PAGE} rokov` : 'Nasledujúci mesiac',
  )

  protected readonly previousDisabled = computed(() => {
    const min = this.min()
    if (!min) return false
    return this.view() === 'years' ? this.pageStart() <= min.getFullYear() : this.visibleMonth() <= monthIndexOf(min)
  })

  protected readonly nextDisabled = computed(() => {
    const max = this.max()
    if (!max) return false
    return this.view() === 'years'
      ? this.pageStart() + YEARS_PER_PAGE - 1 >= max.getFullYear()
      : this.visibleMonth() >= monthIndexOf(max)
  })

  /** The arrows: a month on the days page, 24 years on the years page. */
  protected step(direction: 1 | -1): void {
    this.visibleMonth.update((month) => month + direction * (this.view() === 'years' ? YEARS_PER_PAGE * 12 : 1))
  }

  /** Month ▾ / year ▾: open that page, or go back to the days when it is already open. */
  protected toggle(view: 'months' | 'years'): void {
    this.view.set(this.view() === view ? 'days' : view)
  }

  protected pickDay(cell: DayCell): void {
    if (cell.disabled) return
    this.selectedChange.emit(cell.date)
  }

  protected pickMonth(month: number): void {
    this.visibleMonth.set(this.visibleYear() * 12 + month)
    this.chained.set(false)
    this.showView('days')
  }

  protected pickYear(year: number): void {
    this.visibleMonth.set(year * 12 + (this.visibleMonth() % 12))
    this.showView(this.chained() ? 'months' : 'days')
  }

  /**
   * The cell focus lands on when a page opens, and that `seiri-date-time-picker-panel`'s focus trap starts on: the
   * selected one, else the current one, else the first enabled one.
   */
  protected readonly activeDay = computed(() => this.activeOf(this.days().filter((cell) => cell.inMonth)))
  protected readonly activeMonth = computed(() => this.activeOf(this.months()))
  protected readonly activeYear = computed(() => this.activeOf(this.years()))

  private activeOf<T extends { disabled: boolean; selected: boolean; current: boolean }>(cells: T[]): T | undefined {
    const enabled = cells.filter((cell) => !cell.disabled)
    return enabled.find((cell) => cell.selected) ?? enabled.find((cell) => cell.current) ?? enabled[0]
  }

  /** Switch page and move focus onto it, since the button that was clicked is gone. Only on a user's pick. */
  private showView(view: CalendarView): void {
    this.view.set(view)
    afterNextRender(() => this.host.nativeElement.querySelector<HTMLElement>('[data-active]')?.focus(), {
      injector: this.injector,
    })
  }

  private dayKeyOrNull(date: Date | null | undefined): number | null {
    return date ? dayKeyOf(date) : null
  }

  private isDayOutOfRange(key: number): boolean {
    const min = this.minDay()
    const max = this.maxDay()
    return (min !== null && key < min) || (max !== null && key > max)
  }

  /** Whether every day from `first` to `last` is outside `min`/`max`. */
  private isRangeOutOfBounds(first: Date, last: Date): boolean {
    const min = this.minDay()
    const max = this.maxDay()
    return (min !== null && dayKeyOf(last) < min) || (max !== null && dayKeyOf(first) > max)
  }
}
